import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eyebrow } from "../../components/site/kit";
import { Reveal } from "../../components/site/motion";
import { track } from "../../lib/analytics";
import { GROWTH_SITUATIONS } from "../../lib/content";
import { SITUATION_CASES, SITUATION_ORDER } from "../../lib/homeConfig";
import { useCases } from "../../lib/casesApi";

const byTitle = (title) => GROWTH_SITUATIONS.find((g) => g.title === title) || {};

export const Situations = () => {
  const cases = useCases();
  const [active, setActive] = useState(SITUATION_ORDER[0]);
  const [show, setShow] = useState(true);

  useEffect(() => {
    setShow(false);
    const t = setTimeout(() => setShow(true), 30);
    return () => clearTimeout(t);
  }, [active]);

  const findCase = (slug) => cases.find((c) => c.slug === slug);
  const matched = useMemo(
    () => (SITUATION_CASES[active] || []).map((m) => ({ ...m, data: findCase(m.slug) })),
    [active, cases]
  );
  const meta = byTitle(active);

  const select = (title) => {
    if (title === active) return;
    setActive(title);
    track("home_situation_selected", { situation: title });
  };

  return (
    <section data-testid="home-situations" className="border-b border-white/10 bg-[#161A15]">
      <div className="mx-auto max-w-[1360px] px-6 py-24 md:px-12 md:py-32 lg:px-16">
        <div className="max-w-3xl">
          <Eyebrow dark>What we solve</Eyebrow>
          <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-white md:text-5xl lg:text-6xl">
            Four ways growth gets stuck.
          </h2>
          <p className="font-body mt-6 max-w-2xl text-xl font-normal leading-relaxed text-white/80">
            Pick the one that sounds like your business, and see the work that fixed it.
          </p>
        </div>

        {/* Selector: desktop = 4 across; mobile = horizontally scrollable, tap to select */}
        <div
          role="tablist"
          aria-label="Growth situations"
          className="mt-12 -mx-6 flex snap-x snap-mandatory gap-3 overflow-x-auto px-6 pb-2 md:mx-0 md:grid md:grid-cols-4 md:gap-4 md:overflow-visible md:px-0"
        >
          {SITUATION_ORDER.map((title) => {
            const s = byTitle(title);
            const on = title === active;
            return (
              <button
                key={title}
                role="tab"
                aria-selected={on}
                data-testid={`home-situation-tab-${s.id || title}`}
                onClick={() => select(title)}
                className={`group min-w-[78%] snap-start rounded-lg border p-5 text-left transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5f8f10] sm:min-w-[60%] md:min-w-0 ${
                  on ? "border-[#5f8f10] bg-[#1c2318]" : "border-white/15 bg-transparent hover:border-white/35"
                }`}
              >
                <span className="font-display block text-2xl leading-none text-[#5f8f10]">{s.number}</span>
                <span className={`font-display mt-3 block text-lg uppercase tracking-tight ${on ? "text-white" : "text-white/80"}`}>
                  {title}
                </span>
              </button>
            );
          })}
        </div>

        {/* Active situation blurb + explore */}
        <div className={`mt-10 transition-opacity duration-300 ${show ? "opacity-100" : "opacity-0"}`}>
          <div className="flex flex-col gap-4 border-t border-white/15 pt-8 md:flex-row md:items-end md:justify-between">
            <p className="font-body max-w-2xl text-lg font-normal leading-relaxed text-white/75">{meta.blurb}</p>
            <div className="flex shrink-0 gap-5">
              <Link
                to={meta.href || "/what-we-solve"}
                data-testid="home-situation-explore"
                className="font-body inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:text-[#5f8f10]"
              >
                Explore <span aria-hidden className="text-[#5f8f10]">→</span>
              </Link>
              <Link
                to="/our-work"
                data-testid="home-situation-see-all"
                className="font-body inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold uppercase tracking-[0.12em] text-white/45 transition-colors hover:text-[#5f8f10]"
              >
                See all relevant work <span aria-hidden>→</span>
              </Link>
            </div>
          </div>

          {/* Matched proof: exactly 3 curated cases with one outcome line each */}
          <Reveal className="mt-10 grid gap-6 md:grid-cols-3">
            {matched.map(({ slug, outcome, data }) => (
              <Link
                key={slug}
                to={`/our-work/${slug}`}
                data-testid={`home-matched-case-${slug}`}
                onClick={() => track("home_matched_case_click", { situation: active, slug })}
                className="group flex flex-col overflow-hidden rounded-lg border border-white/12 bg-[#12150F] transition-colors duration-200 hover:border-[#5f8f10]/60"
              >
                <div className="aspect-[16/10] w-full overflow-hidden bg-[#0e1109]">
                  {data?.image && (
                    <img
                      src={data.image}
                      alt={data?.headline || ""}
                      loading="lazy"
                      className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  )}
                </div>
                <div className="flex flex-1 flex-col p-5">
                  {data?.kicker && (
                    <span className="font-body text-xs font-semibold uppercase tracking-[0.14em] text-[#5f8f10]">{data.kicker}</span>
                  )}
                  <p className="font-body mt-3 flex-1 text-base font-normal leading-snug text-white/85">{outcome}</p>
                  <span className="font-body mt-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-white/55 transition-colors group-hover:text-[#5f8f10]">
                    View case <span aria-hidden className="nudge-x text-[#5f8f10]">→</span>
                  </span>
                </div>
              </Link>
            ))}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
